import { useEffect, useRef, useState } from "react";
import api, { errMsg } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { Plus, Trash2, Wrench, CheckCircle2, RotateCcw, Pencil, FileDown, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = () => ({ id: null, item_name: "", warna: "", qty: 1, reason: "", status: "rusak", spareparts: [] });
const cleanSpares = (sps) => (sps || []).filter((s) => (s.name || "").trim()).map((s) => ({ name: s.name, received: !!s.received, received_at: s.received ? s.received_at || null : null }));
const stockLabel = (i) => `${i.name}${i.warna ? ` (${i.warna})` : ""}`;

// searchable picker over Stok items, with free-text fallback (type manually)
const StockCombo = ({ value, items, onSelect, testid, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = items.filter((i) => stockLabel(i).toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <input data-testid={testid} className="nb-input w-full" placeholder={placeholder || "Cari di stok / ketik manual…"}
        value={open ? q : value} onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); onSelect(null, e.target.value); setOpen(true); }} />
      {open && (
        <div data-testid={`${testid}-list`} className="absolute z-30 mt-1 w-full max-h-52 overflow-y-auto bg-white border-2 border-[#0A0A0A] rounded-lg shadow-[3px_3px_0_#0A0A0A]">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-[#52525B]">Tidak ada di stok — ketik manual saja</div>
          ) : filtered.map((i) => (
            <button type="button" key={i.id} onClick={() => { onSelect(i, stockLabel(i)); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 text-sm border-b border-[#0A0A0A]/10 hover:bg-[var(--primary)]">
              {stockLabel(i)} <span className="text-[#52525B]">— stok {i.stock}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Damaged() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/damaged").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (r) => { setForm({ id: r.id, item_name: r.item_name, warna: r.warna || "", qty: r.qty, reason: r.reason || "", status: r.status || "rusak", spareparts: (r.spareparts || []).map((s) => ({ ...s })) }); setOpen(true); };
  const body = (f) => ({ item_name: f.item_name, warna: f.warna, qty: Number(f.qty) || 1, reason: f.reason, status: f.status, spareparts: cleanSpares(f.spareparts) });

  // form pickers
  const pickItem = (item, text) => setForm((f) => (item ? { ...f, item_name: item.name, warna: item.warna || "" } : { ...f, item_name: text }));
  const addSpare = () => setForm((f) => ({ ...f, spareparts: [...f.spareparts, { name: "", received: false, received_at: null }] }));
  const setSpare = (i, v) => setForm((f) => ({ ...f, spareparts: f.spareparts.map((s, idx) => (idx === i ? { ...s, name: v } : s)) }));
  const pickSpare = (i, item, text) => setSpare(i, item ? stockLabel(item) : text);
  const delSpare = (i) => setForm((f) => ({ ...f, spareparts: f.spareparts.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.item_name.trim()) return toast.error("Isi / pilih nama barang");
    try {
      if (form.id) await api.put(`/damaged/${form.id}`, body(form));
      else await api.post("/damaged", body(form));
      toast.success(form.id ? "Data diperbarui" : "Barang rusak dicatat");
      setOpen(false); setForm(emptyForm()); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus catatan rusak: ${r.item_name}?`)) return;
    try { await api.delete(`/damaged/${r.id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const patch = async (r, changes) => {
    try { await api.put(`/damaged/${r.id}`, { ...body(r), ...changes }); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const toggleSpare = (r, idx) => patch(r, { spareparts: cleanSpares((r.spareparts || []).map((s, i) => (i === idx ? { ...s, received: !s.received } : s))) });
  const toggleStatus = (r) => patch(r, { status: r.status === "selesai" ? "rusak" : "selesai" });

  const downloadReport = async (fmt) => {
    try {
      const res = await fetch(`${API}/reports/export?kind=damaged&fmt=${fmt}`, {
        credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan_barang_rusak.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const shown = rows.filter((r) => (statusFilter === "all" || (r.status || "rusak") === statusFilter) && `${r.item_name} ${r.warna || ""}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Barang Rusak</h1>
          <p className="text-[#52525B] text-sm">Tidak masuk stok. Restock manual setelah diperbaiki.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {rows.length > 0 && (
            <>
              <button data-testid="export-damaged-excel" onClick={() => downloadReport("excel")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> Excel</button>
              <button data-testid="export-damaged-pdf" onClick={() => downloadReport("pdf")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> PDF</button>
            </>
          )}
          <button data-testid="add-damaged-button" onClick={openNew} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
            <Plus className="w-5 h-5" strokeWidth={2.5} /> Catat Barang Rusak
          </button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {[["all", "Semua"], ["rusak", "Rusak"], ["selesai", "Selesai"]].map(([v, l]) => (
            <button key={v} data-testid={`damaged-filter-${v}`} onClick={() => setStatusFilter(v)}
              className={`px-3 py-1.5 border-2 border-[#0A0A0A] rounded-lg text-sm font-bold ${statusFilter === v ? "nb-shadow" : "bg-white"}`}
              style={statusFilter === v ? { background: v === "selesai" ? "var(--masuk)" : v === "rusak" ? "var(--retur)" : "var(--primary)", color: v === "rusak" ? "#fff" : "#0A0A0A" } : {}}>{l}</button>
          ))}
          <div className="relative flex-1 min-w-[160px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <input data-testid="damaged-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama barang…" className="nb-input pl-9" />
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <Wrench className="w-10 h-10 mx-auto mb-3 text-[#52525B]" />
          <p className="text-[#52525B]">{rows.length === 0 ? "Belum ada barang rusak tercatat." : "Tidak ada hasil untuk filter / pencarian ini."}</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 items-start">
          {shown.map((r) => {
            const done = r.status === "selesai";
            const sps = r.spareparts || [];
            const gotten = sps.filter((s) => s.received).length;
            return (
              <div key={r.id} data-testid={`damaged-row-${r.id}`} className="nb-card p-4">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg truncate">{r.item_name}{r.warna ? ` (${r.warna})` : ""} × {r.qty}</div>
                    <span className="label-tag inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 border-2 border-[#0A0A0A] rounded" style={{ background: done ? "var(--masuk)" : "var(--retur)", color: done ? "#0A0A0A" : "#fff" }}>
                      {done ? "Selesai Diperbaiki" : "Rusak"}
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`damaged-edit-${r.id}`} onClick={() => openEdit(r)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`damaged-delete-${r.id}`} onClick={() => remove(r)} className="w-8 h-8 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center" style={{ color: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {r.reason && <p className="text-sm mt-2"><span className="label-tag">Alasan rusak:</span> {r.reason}</p>}

                <div className="mt-3 border-t-2 border-[#0A0A0A]/10 pt-3">
                  <div className="label-tag mb-1.5">Sparepart dibutuhkan {sps.length > 0 && `(${gotten}/${sps.length} diterima)`}</div>
                  {sps.length === 0 ? (
                    <p className="text-sm text-[#52525B]">Tidak ada sparepart.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {sps.map((s, i) => (
                        <label key={i} data-testid={`spare-${r.id}-${i}`} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" data-testid={`spare-check-${r.id}-${i}`} checked={!!s.received} onChange={() => toggleSpare(r, i)} className="w-4 h-4 accent-[#2A9D8F] shrink-0" />
                          <span className={`font-semibold ${s.received ? "line-through text-[#52525B]" : ""}`}>{s.name}</span>
                          {s.received && s.received_at && <span className="label-tag ml-auto text-[#2A9D8F]">diterima {s.received_at}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <button data-testid={`damaged-status-${r.id}`} onClick={() => toggleStatus(r)} className="nb-btn w-full py-2 mt-3 bg-white text-sm flex items-center justify-center gap-2">
                  {done ? <><RotateCcw className="w-4 h-4" /> Tandai Masih Rusak</> : <><CheckCircle2 className="w-4 h-4" /> Tandai Selesai Diperbaiki</>}
                </button>
                {done && <p className="text-xs text-[#52525B] mt-2 text-center">Sudah diperbaiki — input ke stok secara manual via Mutasi Stok.</p>}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={form.id ? "Edit Barang Rusak" : "Catat Barang Rusak"} testid="damaged-modal">
        <form onSubmit={save}>
          <Field label="Barang (pilih dari stok / ketik)">
            <StockCombo testid="damaged-name" value={form.item_name} items={items} onSelect={pickItem} placeholder="Cari barang di stok…" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Warna / Varian"><input data-testid="damaged-warna" value={form.warna} onChange={(e) => setForm({ ...form, warna: e.target.value })} className="nb-input" placeholder="mis. Merah" /></Field>
            <Field label="Jumlah"><input data-testid="damaged-qty" type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="nb-input" /></Field>
          </div>
          <Field label="Alasan Rusak"><textarea data-testid="damaged-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="nb-input" rows={2} placeholder="mis. Roda patah, remote tidak berfungsi" /></Field>
          <label className="label-tag block mb-1.5">Sparepart yang Dibutuhkan</label>
          <div className="space-y-2 mb-2">
            {form.spareparts.length === 0 && <p className="text-sm text-[#52525B]">Belum ada. Tambahkan di bawah.</p>}
            {form.spareparts.map((s, i) => (
              <div key={i} className="flex gap-2">
                <div className="flex-1"><StockCombo testid={`damaged-spare-${i}`} value={s.name} items={items} onSelect={(item, text) => pickSpare(i, item, text)} placeholder={`Sparepart #${i + 1} (stok / ketik)`} /></div>
                <button type="button" onClick={() => delSpare(i)} className="w-10 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center shrink-0" style={{ color: "var(--retur)" }}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button type="button" data-testid="add-spare-line" onClick={addSpare} className="text-sm font-bold underline mb-4">+ Tambah sparepart</button>
          <button data-testid="save-damaged-button" type="submit" className="nb-btn w-full py-3 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>{form.id ? "Simpan Perubahan" : "Simpan"}</button>
        </form>
      </Modal>
    </div>
  );
}
